import { useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Popup,
  type Map as MapLibreMap,
  type MapLayerMouseEvent,
} from "maplibre-gl";
import type { Feature, Geometry } from "geojson";
import FeatureService from "mapbox-gl-arcgis-featureserver";
import { DynamicMapService } from "esri-gl";
import { useMapContext } from "@/hooks/useMap";
import {
  TASK_LAYER_KEY,
  TASK_LAYER_IDS,
  TASK_POINT_LAYER_ID,
  TASK_LINE_LAYER_ID,
  TASK_POLYGON_LAYER_ID,
} from "@/constant/layers";
import { LayerService } from "@/services/layerService";
import { useTenant } from "@/hooks/useTenant";
import type { ArcgisLayerConfig } from "@/types/layers";
import { useTaskContext } from "@/context/taskContext";
import type { Task } from "@/types/task";

// Import utilities and types we'll keep
import type { AddLayersManagerProps, TaskDataPayload } from "./types";
import {
  extractAssetFieldOptions,
  buildPopupHtml,
  attachAddTaskHandler,
  applyPopupImageFallbacks,
  fetchArcGISAttachments,
  buildTaskPopupHtml,
  type UnifiedPopupFeature,
} from "./popup";
import { identifyDynamicLayer } from "./identify";
import { fetchAndApplyArcGISStyles } from "./styleConverter";

interface ManagedFeatureService {
  kind: "featureservice";
  service: FeatureService;
  config: ArcgisLayerConfig;
  layerIds: string[];
}

interface ManagedDynamicService {
  kind: "mapservice";
  service: DynamicMapService;
  config: ArcgisLayerConfig;
  sourceId: string;
  layerId: string;
  scaleRanges?: Record<number, { minScale: number; maxScale: number }>;
}

const fetchScaleRanges = async (
  serviceUrl: string
): Promise<Record<number, { minScale: number; maxScale: number }>> => {
  const metadataUrl = serviceUrl.includes("?")
    ? `${serviceUrl}&f=pjson`
    : `${serviceUrl}?f=pjson`;

  try {
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(metadataUrl, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      console.debug(
        `[Layer Order] Failed to fetch metadata for ${serviceUrl}: ${response.status}`
      );
      return {};
    }

    const data = (await response.json()) as {
      layers?: Array<{
        id: number;
        minScale?: number;
        maxScale?: number;
      }>;
    };

    if (!data.layers || data.layers.length === 0) {
      return {};
    }

    return data.layers.reduce<
      Record<number, { minScale: number; maxScale: number }>
    >((acc, layer) => {
      acc[layer.id] = {
        minScale: layer.minScale ?? 0,
        maxScale: layer.maxScale ?? 0,
      };
      return acc;
    }, {});
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      console.warn(
        `[Layer Order] Timeout fetching metadata for ${serviceUrl} - continuing without scale ranges`
      );
    } else {
      console.debug(
        `[Layer Order] Error fetching metadata for ${serviceUrl}:`,
        error
      );
    }
    return {};
  }
};

export const AddLayersManager: React.FC<AddLayersManagerProps> = ({
  onAddTask,
  onShowGeometrySelection,
}) => {
  const {
    mapInstance,
    getVisibleArcgisLayers,
    arcgisLayersVisible,
    taskLayerVisible,
    isDrawingRef, // Get ref from context for synchronous access
    setHighlightedFeature,
  } = useMapContext();

  // Access tasks from TaskContext
  const { tasks } = useTaskContext();
  const { tenantId } = useTenant();

  const { data: allLayerConfigs = [] } = useQuery({
    queryKey: ["asset-layers", tenantId],
    queryFn: () => LayerService.getAllLayers(tenantId!),
    enabled: !!tenantId,
  });

  // Ref to store managed services
  const managedServicesRef = useRef<
    Record<string, ManagedFeatureService | ManagedDynamicService>
  >({});
  const popupRef = useRef<Popup | null>(null);
  const highlightFeatureIdRef = useRef<string | null>(null);
  // Updated refs for unified popup
  const currentUnifiedFeaturesRef = useRef<UnifiedPopupFeature[]>([]);
  const currentPageRef = useRef<number>(0);
  const currentConfigRef = useRef<ArcgisLayerConfig | null>(null);
  const currentCoordsRef = useRef<{ lng: number; lat: number } | null>(null);
  const addingLayersRef = useRef<Set<string>>(new Set()); // Track layers being added

  const closePopup = useCallback(() => {
    popupRef.current?.remove();
    popupRef.current = null;
    highlightFeatureIdRef.current = null;
  }, []);

  const taskEnabledLayerKeys = useMemo(() => {
    const layers = getVisibleArcgisLayers()
      .filter((layer) => layer.key !== TASK_LAYER_KEY)
      .filter((layer) => arcgisLayersVisible[layer.key] ?? layer.visible);

    const enabled = layers.filter((layer) => layer.task_enabled);

    if (enabled.length > 0) {
      return new Set(enabled.map((layer) => layer.key));
    }

    // Fallback default layers
    return new Set(["bridge_inventory", "conduit_inventory", "road_inventory"]);
  }, [arcgisLayersVisible, getVisibleArcgisLayers]);

  // New unified popup function that handles all feature types
  const showUnifiedPopup = useCallback(
    async (
      map: MapLibreMap,
      unifiedFeatures: UnifiedPopupFeature[],
      lng: number,
      lat: number,
      page: number = 0
    ) => {
      if (unifiedFeatures.length === 0) return;

      closePopup();

      // Store current state for pagination
      currentUnifiedFeaturesRef.current = unifiedFeatures;
      currentPageRef.current = page;
      currentCoordsRef.current = { lng, lat };

      const feature = unifiedFeatures[page];
      const totalPages = unifiedFeatures.length;

      // Build popup HTML based on feature type
      let popupHtml: string;
      let buttonId: string | null = null;

      if (feature.type === "task" && feature.task) {
        // Task feature - use task-specific popup
        popupHtml = buildTaskPopupHtml(feature.task, page, totalPages);
      } else {
        // ArcGIS or dynamic feature
        const taskEnabled = feature.taskEnabled || false;
        buttonId = taskEnabled
          ? `add-task-btn-${feature.layerKey}-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`
          : null;

        // Fetch attachments for ArcGIS features
        let attachmentUrl: string | null = null;
        if (feature.objectId && feature.layerUrl) {
          attachmentUrl = await fetchArcGISAttachments(
            feature.layerUrl,
            feature.objectId
          );
        }

        popupHtml = buildPopupHtml(
          feature.title,
          feature.properties,
          buttonId,
          taskEnabled,
          page,
          totalPages,
          attachmentUrl
        );
      }

      const popup = new Popup({
        closeButton: false,
        maxWidth: "420px",
      })
        .setLngLat([lng, lat])
        .setHTML(popupHtml)
        .addTo(map);

      // Apply image loading fallbacks
      const popupEl = popup.getElement();
      if (popupEl) {
        applyPopupImageFallbacks(popupEl);
      }

      popupRef.current = popup;
      highlightFeatureIdRef.current = String(feature.objectId ?? "");

      popup.on("close", () => {
        if (popupRef.current === popup) {
          popupRef.current = null;
        }
        highlightFeatureIdRef.current = null;
        currentUnifiedFeaturesRef.current = [];
        currentPageRef.current = 0;
        currentConfigRef.current = null;
        currentCoordsRef.current = null;
        map.getCanvas().style.cursor = "";
      });

      // Attach handlers
      requestAnimationFrame(() => {
        const element = popup.getElement();

        // Close button
        const closeBtn =
          element?.querySelector<HTMLButtonElement>("#arcgis-close-btn");
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            popup.remove();
          });
        }

        // View Task button for task features
        const viewTaskBtn =
          element?.querySelector<HTMLButtonElement>("#view-task-btn");
        if (viewTaskBtn && feature.type === "task" && feature.task) {
          viewTaskBtn.addEventListener("click", () => {
            // Store the task for external access and dispatch custom event
            (window as { __unifiedPopupTask?: Task }).__unifiedPopupTask =
              feature.task;
            window.dispatchEvent(
              new CustomEvent("unified-popup-view-task", {
                detail: { task: feature.task },
              })
            );
          });
        }

        // Pagination for multiple features
        if (totalPages > 1) {
          const prevBtn =
            element?.querySelector<HTMLButtonElement>("#arcgis-prev-page");
          const nextBtn =
            element?.querySelector<HTMLButtonElement>("#arcgis-next-page");

          if (prevBtn) {
            prevBtn.addEventListener("click", () => {
              if (page > 0) {
                showUnifiedPopup(map, unifiedFeatures, lng, lat, page - 1);
              }
            });
          }

          if (nextBtn) {
            nextBtn.addEventListener("click", () => {
              if (page < totalPages - 1) {
                showUnifiedPopup(map, unifiedFeatures, lng, lat, page + 1);
              }
            });
          }
        }
      });

      // Add Task button handler for ArcGIS features
      if (buttonId && feature.layerKey) {
        const layerConfig = allLayerConfigs.find(
          (c) => c.key === feature.layerKey
        );
        if (layerConfig) {
          const assetOptions = extractAssetFieldOptions(
            layerConfig.asset_fields,
            feature.properties
          );
          const defaultAssetField = assetOptions[0]?.field ?? "";
          const defaultAssetId = assetOptions[0]?.values[0] ?? "";
          const skipAssetAllowed = layerConfig.skip_asset_allowed !== false;
          const geometry =
            feature.geometry ??
            ({
              type: "Point",
              coordinates: [lng, lat],
            } as Geometry);

          const taskData: TaskDataPayload = {
            lat: String(lat),
            lng: String(lng),
            layerName: layerConfig.key,
            asset_field: defaultAssetField,
            asset_id: defaultAssetId,
            skip_asset_allowed: skipAssetAllowed,
            geometry,
          };

          attachAddTaskHandler(popup, buttonId, () => {
            onShowGeometrySelection?.(taskData);
            onAddTask?.(taskData);
            closePopup();
          });
        }
      }
    },
    [closePopup, onAddTask, onShowGeometrySelection, allLayerConfigs]
  );

  // Handle map clicks to identify features from ALL layers (unified popup)
  const handleMapClick = useCallback(
    async (e: MapLayerMouseEvent) => {
      const map = mapInstance.current;
      if (!map || isDrawingRef.current) {
        return;
      }

      // Don't show popup when other handlers prevent default
      if (e.defaultPrevented || e.originalEvent?.defaultPrevented) {
        return;
      }

      const allUnifiedFeatures: UnifiedPopupFeature[] = [];
      const allRenderedFeatures = map.queryRenderedFeatures(e.point);

      // 1. Collect Task features
      if (taskLayerVisible) {
        const taskLayerIds = [
          TASK_POINT_LAYER_ID,
          TASK_LINE_LAYER_ID,
          TASK_POLYGON_LAYER_ID,
        ];
        const taskFeatures = allRenderedFeatures.filter((f) =>
          taskLayerIds.some((layerId) => {
            try {
              return map.getLayer(layerId) && f.layer.id === layerId;
            } catch {
              return false;
            }
          })
        );

        for (const feature of taskFeatures) {
          const taskId = feature.properties?.taskId;
          const task = tasks.find((t) => t.id === taskId);
          if (task) {
            allUnifiedFeatures.push({
              type: "task",
              title: task.title || "Task",
              properties: feature.properties as Record<string, unknown>,
              task,
              objectId: task.id,
              geometry: feature.geometry,
            });
          }
        }
      }

      // 2. Collect ArcGIS FeatureServer features
      for (const feature of allRenderedFeatures) {
        const layerKey = Object.keys(managedServicesRef.current).find((key) =>
          feature.layer.id.startsWith(`arcgis-${key}`)
        );

        if (layerKey) {
          const managed = managedServicesRef.current[layerKey];
          if (managed && managed.kind === "featureservice") {
            allUnifiedFeatures.push({
              type: "arcgis",
              title: managed.config.label,
              properties: (feature.properties ?? {}) as Record<string, unknown>,
              layerKey: layerKey,
              layerUrl: managed.config.url,
              objectId: feature.properties?.OBJECTID || feature.id,
              taskEnabled: taskEnabledLayerKeys.has(layerKey),
              geometry: feature.geometry,
            });
          }
        }
      }

      // 3. Collect ArcGIS MapServer (dynamic) features via identify
      const dynamicIdentifyPromises: Promise<void>[] = [];
      for (const [key, managed] of Object.entries(managedServicesRef.current)) {
        if (managed.kind === "mapservice") {
          const identifyPromise = identifyDynamicLayer(
            map,
            managed.config,
            e.lngLat.lng,
            e.lngLat.lat,
            managed.scaleRanges
          )
            .then((results) => {
              for (const result of results) {
                allUnifiedFeatures.push({
                  type: "dynamic",
                  title: managed.config.label,
                  properties: (result.properties ?? {}) as Record<
                    string,
                    unknown
                  >,
                  layerKey: key,
                  layerUrl: managed.config.url,
                  objectId: result.properties?.OBJECTID || result.id,
                  taskEnabled: taskEnabledLayerKeys.has(key),
                  geometry: result.geometry,
                });
              }
            })
            .catch((error) => {
              console.warn(`Failed to identify MapServer layer ${key}:`, error);
            });
          dynamicIdentifyPromises.push(identifyPromise);
        }
      }

      // If we already have non-dynamic features (tasks, ArcGIS FeatureServer),
      // show popup immediately without waiting for slow dynamic identifies.
      // Dynamic features will be skipped to avoid blocking the popup.
      if (dynamicIdentifyPromises.length > 0 && allUnifiedFeatures.length === 0) {
        // Only wait for dynamic identifies if we have no other features yet
        await Promise.race([
          Promise.all(dynamicIdentifyPromises),
          new Promise((resolve) => setTimeout(resolve, 10000)),
        ]);
      } else if (dynamicIdentifyPromises.length > 0) {
        // Fire-and-forget: don't block popup for dynamic identifies
        Promise.all(dynamicIdentifyPromises).catch(() => {});
      }

      // If we found any features, show unified popup
      if (allUnifiedFeatures.length > 0) {
        // Set first feature as highlighted
        const firstFeature = allUnifiedFeatures[0];
        if (firstFeature.type === "task") {
          setHighlightedFeature({
            feature: {
              type: "Feature",
              geometry: firstFeature.geometry,
              properties: firstFeature.properties,
            } as Feature<Geometry>,
            layerType: "task",
          });
        } else {
          setHighlightedFeature({
            feature: {
              type: "Feature",
              geometry: firstFeature.geometry,
              properties: firstFeature.properties,
            } as Feature<Geometry>,
            layerType: firstFeature.type === "dynamic" ? "dynamic" : "arcgis",
            sourceId: firstFeature.layerKey,
          });
        }

        await showUnifiedPopup(
          map,
          allUnifiedFeatures,
          e.lngLat.lng,
          e.lngLat.lat
        );
      }
    },
    [
      mapInstance,
      isDrawingRef,
      showUnifiedPopup,
      setHighlightedFeature,
      taskLayerVisible,
      tasks,
      taskEnabledLayerKeys,
    ]
  );

  // Position layers according to display_order
  const reorderAllLayers = useCallback(
    (map: MapLibreMap) => {
      if (!map || allLayerConfigs.length === 0) return;

      try {
        // ========== UNIFIED LAYER ORDERING SYSTEM ==========
        // Get ALL visible layers including task layer
        const arcgisVisibleLayers = getVisibleArcgisLayers()
          .map((layer) => ({
            ...layer,
            visible: arcgisLayersVisible[layer.key] ?? layer.visible,
          }))
          .filter((layer) => layer.visible);

        const combinedLayers = [...arcgisVisibleLayers];

        const taskLayerConfig = allLayerConfigs.find(
          (config) => config.key === TASK_LAYER_KEY
        );

        if (taskLayerConfig && taskLayerVisible) {
          combinedLayers.push({
            ...taskLayerConfig,
            visible: true,
          });
        }

        // Sort by display_order descending (high to low)
        // Lower display_order = higher in visual stack (on top)
        const sortedConfigs = combinedLayers.sort(
          (a, b) => (b.display_order ?? 999) - (a.display_order ?? 999)
        );

        console.debug(
          "[Layer Order] UNIFIED reordering ALL layers (low to high):",
          sortedConfigs.map((c) => `${c.key}(${c.display_order})`)
        );

        // Move layers in order from lowest to highest display_order
        // Since moveLayer() moves each to top, lowest display_order ends up on top
        sortedConfigs.forEach((config) => {
          // Handle task layer
          if (config.key === TASK_LAYER_KEY) {
            TASK_LAYER_IDS.forEach((layerId) => {
              try {
                if (map.getLayer(layerId)) {
                  map.moveLayer(layerId);
                  console.debug(
                    `[Layer Order] Moved task layer ${layerId} to top (display_order: ${config.display_order})`
                  );
                } else {
                  console.debug(
                    `[Layer Order] Task layer ${layerId} not found on map yet`
                  );
                }
              } catch (error) {
                console.warn(`Failed to move task layer ${layerId}:`, error);
              }
            });
            return;
          }

          // Handle ArcGIS layers
          const managed = managedServicesRef.current[config.key];
          if (managed) {
            if (managed.kind === "featureservice") {
              // FeatureServer has multiple layers
              managed.layerIds.forEach((layerId) => {
                try {
                  if (map.getLayer(layerId)) {
                    map.moveLayer(layerId);
                    console.debug(
                      `[Layer Order] Moved FeatureServer ${layerId} to top (display_order: ${config.display_order})`
                    );
                  }
                } catch (error) {
                  console.warn(`Failed to move layer ${layerId}:`, error);
                }
              });
            } else {
              // MapServer has single layer
              try {
                if (map.getLayer(managed.layerId)) {
                  map.moveLayer(managed.layerId);
                  console.debug(
                    `[Layer Order] Moved MapServer ${managed.layerId} to top (display_order: ${config.display_order})`
                  );
                }
              } catch (error) {
                console.warn(`Failed to move layer ${managed.layerId}:`, error);
              }
            }
          }
        });

        // Notify other components that layers were reordered
        window.dispatchEvent(
          new CustomEvent("arcgis-layers-reordered", { detail: { map } })
        );

        // CRITICAL: Ensure Mapbox Draw layers stay on top of everything
        // Draw layers are created dynamically and must always be visible above all other layers
        const allLayers: Array<{ id: string }> =
          map.getStyle().layers || [];
        const drawLayerIds: string[] = allLayers
          .map((layer) => layer.id)
          .filter((id) => {
            const lower = id.toLowerCase();
            return (
              lower.includes("gl-draw") ||
              lower.includes("draw-layer") ||
              lower.startsWith("drawn-layer-")
            );
          });

        if (drawLayerIds.length > 0) {
          console.debug(
            "[Layer Order] Found Mapbox Draw layers, moving to absolute top:",
            drawLayerIds
          );
          drawLayerIds.forEach((layerId: string) => {
            try {
              if (map.getLayer(layerId)) {
                map.moveLayer(layerId);
                console.debug(
                  `[Layer Order] Moved Mapbox Draw layer ${layerId} to top`
                );
              }
            } catch (error) {
              console.warn(`Failed to move draw layer ${layerId}:`, error);
            }
          });
        } else {
          console.debug(
            "[Layer Order] No Mapbox Draw layers found during reorder"
          );
        }
      } catch (error) {
        console.error("Failed to reorder layers:", error);
      }
    },
    [
      allLayerConfigs,
      getVisibleArcgisLayers,
      arcgisLayersVisible,
      taskLayerVisible,
    ]
  );

  const syncVisibleLayers = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    const visibleLayers = getVisibleArcgisLayers()
      .filter((layer) => layer.key !== TASK_LAYER_KEY)
      .map((layer) => ({
        ...layer,
        visible: arcgisLayersVisible[layer.key] ?? layer.visible,
      }))
      .filter((layer) => layer.visible);

    const nextKeys = new Set(visibleLayers.map((layer) => layer.key));

    // Remove layers that are no longer visible
    Object.keys(managedServicesRef.current).forEach((key) => {
      if (!nextKeys.has(key)) {
        const managed = managedServicesRef.current[key];
        try {
          if (managed.kind === "featureservice") {
            // Remove all associated layers FIRST (before removing source)
            managed.layerIds.forEach((layerId) => {
              try {
                if (map.getLayer(layerId)) {
                  map.removeLayer(layerId);
                }
              } catch (error) {
                console.warn(`Failed to remove layer ${layerId}:`, error);
              }
            });

            // THEN destroy the FeatureService (removes source and listeners)
            managed.service.destroySource();
          } else {
            // MapServer cleanup
            try {
              if (map.getLayer(managed.layerId)) {
                map.removeLayer(managed.layerId);
              }
            } catch (error) {
              console.warn(`Failed to remove layer ${managed.layerId}:`, error);
            }

            try {
              if (map.getSource(managed.sourceId)) {
                map.removeSource(managed.sourceId);
              }
            } catch (error) {
              console.warn(
                `Failed to remove source ${managed.sourceId}:`,
                error
              );
            }
          }

          delete managedServicesRef.current[key];
        } catch (error) {
          console.warn(`Failed to cleanup service for ${key}:`, error);
        }
      }
    });

    // Add new layers
    const additions = visibleLayers
      .filter(
        (config) =>
          !managedServicesRef.current[config.key] &&
          !addingLayersRef.current.has(config.key)
      )
      .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));

    // Process additions (handle async FeatureServer style fetching)
    const addLayersAsync = async () => {
      for (const config of additions) {
        // Mark as being added
        addingLayersRef.current.add(config.key);

        try {
          // Handle MapServer (dynamic layers)
          if (config.service_type === "mapservice") {
            const sourceId = `arcgis-${config.key}-source`;
            const layerId = `${sourceId}-layer`;

            // Skip if already exists
            if (map.getSource(sourceId)) {
              addingLayersRef.current.delete(config.key);
              continue;
            }

            let service;
            let scaleRanges = {};

            try {
              service = new DynamicMapService(
                sourceId,
                map,
                {
                  url: config.url,
                  layers:
                    config.layer_numbers && config.layer_numbers.length > 0
                      ? config.layer_numbers
                      : undefined,
                  transparent: true,
                  format: "png32",
                },
                {
                  attribution: "",
                }
              );

              // Fetch scale ranges with timeout handling
              scaleRanges = await fetchScaleRanges(config.url);
            } catch (error) {
              console.error(
                `Failed to initialize MapServer for ${config.key}:`,
                error
              );
              addingLayersRef.current.delete(config.key);
              continue;
            }

            // Wait for the source to be added by DynamicMapService
            // The service adds the source asynchronously, so we need to wait
            let sourceAdded = false;
            let attempts = 0;
            const maxAttempts = 20; // Wait up to 2 seconds (20 * 100ms)

            while (!sourceAdded && attempts < maxAttempts) {
              if (map.getSource(sourceId)) {
                sourceAdded = true;
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 100));
              attempts++;
            }

            if (!sourceAdded) {
              console.error(
                `MapServer source ${sourceId} was not created after waiting`
              );
              addingLayersRef.current.delete(config.key);
              continue;
            }

            if (!map.getLayer(layerId)) {
              map.addLayer({
                id: layerId,
                type: "raster",
                source: sourceId,
                paint: {
                  "raster-opacity": config.opacity ?? 1.0,
                },
              });
            }

            managedServicesRef.current[config.key] = {
              kind: "mapservice",
              service,
              config,
              sourceId,
              layerId,
              scaleRanges,
            };

            addingLayersRef.current.delete(config.key);
            setTimeout(() => reorderAllLayers(map), 100);
            continue;
          }

          // Handle FeatureServer
          if (config.service_type === "featureservice") {
            const sourceId = `arcgis-${config.key}-source`;

            // Skip if already exists
            if (map.getSource(sourceId)) {
              addingLayersRef.current.delete(config.key);
              continue;
            }

            // Create the FeatureService
            const service = new FeatureService(sourceId, map, {
              url: config.url,
              minZoom: 0, // Allow viewing at all zoom levels
              simplifyFactor: 0.3,
              precision: 6,
              useServiceBounds: true,
              where: "1=1",
              outFields: "*",
            });

            // Fetch and apply ArcGIS styles
            const { layerIds } = await fetchAndApplyArcGISStyles(
              config,
              sourceId,
              map
            );

            // Store the managed service
            managedServicesRef.current[config.key] = {
              kind: "featureservice",
              service,
              config,
              layerIds,
            };

            addingLayersRef.current.delete(config.key);
            // Reorder after adding
            setTimeout(() => reorderAllLayers(map), 100);
            continue;
          }

          // Unknown service type
          console.warn(
            `Layer ${config.key} has unknown service_type: ${config.service_type}`
          );
          addingLayersRef.current.delete(config.key);
        } catch (error) {
          console.error(`Failed to add layer ${config.key}:`, error);
          addingLayersRef.current.delete(config.key);
        }
      }
    };

    // Execute async layer addition
    addLayersAsync();
  }, [
    mapInstance,
    getVisibleArcgisLayers,
    arcgisLayersVisible,
    reorderAllLayers,
  ]);

  // Main layer management effect
  useEffect(() => {
    syncVisibleLayers();
  }, [syncVisibleLayers]);

  // Rebuild ArcGIS layers after basemap/style switches
  useEffect(() => {
    const handleBasemapChange = () => {
      const map = mapInstance.current;
      if (!map) {
        return;
      }

      closePopup();

      Object.values(managedServicesRef.current).forEach((managed) => {
        try {
          if (managed.kind === "featureservice") {
            // Remove layers FIRST before destroying source
            managed.layerIds.forEach((layerId) => {
              try {
                if (map.getLayer(layerId)) {
                  map.removeLayer(layerId);
                }
              } catch (error) {
                console.warn(`Failed to remove layer ${layerId}:`, error);
              }
            });
            // THEN destroy source (only if it still exists)
            try {
              managed.service.destroySource();
            } catch (error) {
              // Source may have already been removed by basemap style change
              console.warn(
                `Failed to destroy source for ${managed.config.key}:`,
                error
              );
            }
          } else {
            // Remove layer if it exists
            try {
              if (map.getLayer(managed.layerId)) {
                map.removeLayer(managed.layerId);
              }
            } catch (error) {
              console.warn(`Failed to remove layer ${managed.layerId}:`, error);
            }
            // Remove source if it exists
            try {
              if (map.getSource(managed.sourceId)) {
                map.removeSource(managed.sourceId);
              }
            } catch (error) {
              console.warn(
                `Failed to remove source ${managed.sourceId}:`,
                error
              );
            }
          }
        } catch (error) {
          console.warn(
            "Failed to clean up ArcGIS layer during basemap change:",
            error
          );
        }
      });

      managedServicesRef.current = {};
      addingLayersRef.current.clear();

      // Allow the new style a moment to settle before re-syncing layers
      window.setTimeout(() => {
        syncVisibleLayers();
      }, 0);
    };

    window.addEventListener("basemap-changed", handleBasemapChange);

    return () => {
      window.removeEventListener("basemap-changed", handleBasemapChange);
    };
  }, [mapInstance, syncVisibleLayers, closePopup]);

  // Register click handler
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    map.on("click", handleMapClick);

    return () => {
      map.off("click", handleMapClick);
    };
  }, [mapInstance, handleMapClick]);

  // Handle display order changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    // Reorder after a short delay to ensure layers are loaded
    const timeoutId = setTimeout(() => reorderAllLayers(map), 100);

    return () => clearTimeout(timeoutId);
  }, [mapInstance, reorderAllLayers, allLayerConfigs, arcgisLayersVisible]);

  // Listen for external layer reordering events
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const handleTaskLayersRepositioned = () => {
      setTimeout(() => reorderAllLayers(map), 50);
    };

    window.addEventListener(
      "task-layers-repositioned",
      handleTaskLayersRepositioned
    );

    return () => {
      window.removeEventListener(
        "task-layers-repositioned",
        handleTaskLayersRepositioned
      );
    };
  }, [mapInstance, reorderAllLayers]);

  // Cleanup on unmount
  useEffect(() => {
    const map = mapInstance.current;

    return () => {
      closePopup();

      // Clean up all services
      Object.values(managedServicesRef.current).forEach((managed) => {
        try {
          if (managed.kind === "featureservice") {
            // Remove layers FIRST before destroying source
            managed.layerIds.forEach((layerId) => {
              try {
                if (map && map.getLayer(layerId)) {
                  map.removeLayer(layerId);
                }
              } catch (error) {
                console.warn(`Failed to remove layer ${layerId}:`, error);
              }
            });
            // THEN destroy source
            managed.service.destroySource();
          }
          // MapServer cleanup handled by maplibre when layers are removed
        } catch (error) {
          console.warn("Failed to destroy service:", error);
        }
      });

      managedServicesRef.current = {};
    };
  }, [closePopup, mapInstance]);

  return null;
};
